import React, { useState, useEffect } from 'react';
import {
  View,
  Modal,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../../common/Text';
import { useTheme } from '../../../hooks/useTheme';
import { useUserDetails } from '../../../hooks/useAdminData';
import { palette } from '../../../utils/palette';

/** Shape of user details from API - used for type documentation */
interface UserDetail {
  id: number;
  email: string;
  name?: string;
  is_active: boolean;
  is_admin: boolean;
  role: string;
  account_status: string;
  age_verified: boolean;
  age_verified_at?: string;
  age_bracket?: string;
  subscription_plan: string;
  subscription_status: string;
  subscription_expires_at?: string;
  profile_image_url?: string;
  bio?: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
  colour_profiles: Array<{ id: number; name: string; created_at: string }>;
  moodboards: Array<{
    id: number;
    name: string;
    is_public: boolean;
    likes_count: number;
    views_count: number;
    created_at: string;
  }>;
  activity_logs: Array<{
    id: number;
    action: string;
    action_type: string;
    details: any;
    ip_address?: string;
    created_at: string;
  }>;
  stats: {
    colour_profiles_count: number;
    moodboards_count: number;
    total_activity: number;
  };
}

interface UserDetailModalProps {
  visible: boolean;
  userId: number | null;
  onClose: () => void;
  onRefresh?: () => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  visible,
  userId,
  onClose,
  onRefresh: _onRefresh,
}) => {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<'overview' | 'activity'>(
    'overview'
  );
  const scrollViewRef = React.useRef<ScrollView>(null);

  // Use TanStack Query to fetch user details
  const {
    data: userDetail,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useUserDetails(visible && userId ? userId : null) as {
    data: UserDetail | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
  };

  const error = queryError ? (queryError as Error).message : null;

  // Refetch when modal becomes visible with a userId
  useEffect(() => {
    if (visible && userId) {
      refetch();
    }
  }, [visible, userId, refetch]);

  // Reset scroll position when tab changes
  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: false });
    }
  }, [activeTab]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    const colors_map: { [key: string]: string } = {
      active: palette.cyanLimeGreen,
      suspended: palette.lightOrange,
      deleted: palette.brightMatteRed,
    };
    return colors_map[status] || colors.text;
  };

  const getRoleColor = (role: string) => {
    const colors_map: { [key: string]: string } = {
      admin: palette.brightGreen,
      moderator: palette.lightOrange,
      user: palette.darkGrayBlue,
    };
    return colors_map[role] || colors.text;
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={[styles.overlay, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}>
        <View style={[styles.modal, { backgroundColor: colors.background }]}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: colors.border }]}>
            <Text style={[styles.title, { color: colors.text }]}>
              User Details
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={[styles.loadingText, { color: colors.text }]}>
                Loading user details...
              </Text>
            </View>
          ) : error ? (
            <View style={styles.errorContainer}>
              <Ionicons
                name="alert-circle"
                size={48}
                color={palette.brightMatteRed}
              />
              <Text
                style={[styles.errorText, { color: palette.brightMatteRed }]}
              >
                {error}
              </Text>
              <TouchableOpacity
                onPress={() => refetch()}
                style={[
                  styles.retryButton,
                  { backgroundColor: colors.primary },
                ]}
              >
                <Text
                  style={[styles.retryButtonText, { color: palette.white }]}
                >
                  Retry
                </Text>
              </TouchableOpacity>
            </View>
          ) : userDetail ? (
            <ScrollView
              ref={scrollViewRef}
              style={styles.content}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1 }}
            >
              {/* Tabs */}
              <View style={[styles.tabs, { borderBottomColor: colors.border }]}>
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === 'overview' && [
                      styles.activeTab,
                      { borderBottomColor: colors.primary },
                    ],
                  ]}
                  onPress={() => setActiveTab('overview')}
                >
                  <Text
                    style={[
                      styles.tabText,
                      {
                        color:
                          activeTab === 'overview'
                            ? colors.primary
                            : colors.secondary,
                      },
                    ]}
                  >
                    Overview
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === 'activity' && [
                      styles.activeTab,
                      { borderBottomColor: colors.primary },
                    ],
                  ]}
                  onPress={() => setActiveTab('activity')}
                >
                  <Text
                    style={[
                      styles.tabText,
                      {
                        color:
                          activeTab === 'activity'
                            ? colors.primary
                            : colors.secondary,
                      },
                    ]}
                  >
                    Activity ({userDetail.activity_logs.length})
                  </Text>
                </TouchableOpacity>
              </View>

              {activeTab === 'overview' ? (
                <View style={styles.overview}>
                  {/* Basic Info */}
                  <View
                    style={[styles.section, { backgroundColor: colors.card }]}
                  >
                    <Text style={[styles.sectionTitle, { color: colors.text }]}>
                      Basic Information
                    </Text>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Email:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {userDetail.email}
                      </Text>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Name:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {userDetail.name || 'N/A'}
                      </Text>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Role:
                      </Text>
                      <View
                        style={[
                          styles.badge,
                          {
                            backgroundColor: `${getRoleColor(userDetail.role)}20`,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.badgeText,
                            { color: getRoleColor(userDetail.role) },
                          ]}
                        >
                          {userDetail.role}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Account Status:
                      </Text>
                      <View
                        style={[
                          styles.badge,
                          {
                            backgroundColor: `${getStatusColor(userDetail.account_status)}20`,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.badgeText,
                            {
                              color: getStatusColor(userDetail.account_status),
                            },
                          ]}
                        >
                          {userDetail.account_status}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Age Verified:
                      </Text>
                      <Text
                        style={[
                          styles.value,
                          {
                            color: userDetail.age_verified
                              ? palette.cyanLimeGreen
                              : palette.darkGrayBlue,
                          },
                        ]}
                      >
                        {userDetail.age_verified
                          ? '✓ Verified'
                          : 'Not verified'}
                      </Text>
                    </View>
                    {userDetail.age_verified_at && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[styles.label, { color: colors.secondary }]}
                        >
                          Verified At:
                        </Text>
                        <Text style={[styles.value, { color: colors.text }]}>
                          {formatDate(userDetail.age_verified_at)}
                        </Text>
                      </View>
                    )}
                    {userDetail.age_bracket && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[styles.label, { color: colors.secondary }]}
                        >
                          Age Bracket:
                        </Text>
                        <Text style={[styles.value, { color: colors.text }]}>
                          {userDetail.age_bracket}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Subscription Info */}
                  <View
                    style={[styles.section, { backgroundColor: colors.card }]}
                  >
                    <Text style={[styles.sectionTitle, { color: colors.text }]}>
                      Subscription
                    </Text>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Plan:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {userDetail.subscription_plan}
                      </Text>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Status:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {userDetail.subscription_status}
                      </Text>
                    </View>
                    {userDetail.subscription_expires_at && (
                      <View style={styles.infoRow}>
                        <Text
                          style={[styles.label, { color: colors.secondary }]}
                        >
                          Expires:
                        </Text>
                        <Text style={[styles.value, { color: colors.text }]}>
                          {formatDate(userDetail.subscription_expires_at)}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Stats */}
                  <View
                    style={[styles.section, { backgroundColor: colors.card }]}
                  >
                    <Text style={[styles.sectionTitle, { color: colors.text }]}>
                      Statistics
                    </Text>
                    <View style={styles.statsGrid}>
                      <View style={styles.statItem}>
                        <Text
                          style={[styles.statValue, { color: colors.primary }]}
                        >
                          {userDetail.stats.colour_profiles_count}
                        </Text>
                        <Text
                          style={[
                            styles.statLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Colour Profiles
                        </Text>
                      </View>
                      <View style={styles.statItem}>
                        <Text
                          style={[styles.statValue, { color: colors.primary }]}
                        >
                          {userDetail.stats.moodboards_count}
                        </Text>
                        <Text
                          style={[
                            styles.statLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Moodboards
                        </Text>
                      </View>
                      <View style={styles.statItem}>
                        <Text
                          style={[styles.statValue, { color: colors.primary }]}
                        >
                          {userDetail.stats.total_activity}
                        </Text>
                        <Text
                          style={[
                            styles.statLabel,
                            { color: colors.secondary },
                          ]}
                        >
                          Activity Logs
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Colour Profiles */}
                  {userDetail.colour_profiles.length > 0 && (
                    <View
                      style={[styles.section, { backgroundColor: colors.card }]}
                    >
                      <Text
                        style={[styles.sectionTitle, { color: colors.text }]}
                      >
                        Colour Profiles ({userDetail.colour_profiles.length})
                      </Text>
                      {userDetail.colour_profiles.map((profile: any) => (
                        <View
                          key={profile.id}
                          style={[
                            styles.listItem,
                            { borderBottomColor: colors.border },
                          ]}
                        >
                          <Text
                            style={[
                              styles.listItemText,
                              { color: colors.text },
                            ]}
                          >
                            {profile.name}
                          </Text>
                          <Text
                            style={[
                              styles.listItemDate,
                              { color: colors.secondary },
                            ]}
                          >
                            {formatDate(profile.created_at)}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Moodboards */}
                  {userDetail.moodboards.length > 0 && (
                    <View
                      style={[styles.section, { backgroundColor: colors.card }]}
                    >
                      <Text
                        style={[styles.sectionTitle, { color: colors.text }]}
                      >
                        Moodboards ({userDetail.moodboards.length})
                      </Text>
                      {userDetail.moodboards.map((mb: any) => (
                        <View
                          key={mb.id}
                          style={[
                            styles.listItem,
                            { borderBottomColor: colors.border },
                          ]}
                        >
                          <View style={styles.moodboardInfo}>
                            <Text
                              style={[
                                styles.listItemText,
                                { color: colors.text },
                              ]}
                            >
                              {mb.name}
                            </Text>
                            <View style={styles.moodboardStats}>
                              <Text
                                style={[
                                  styles.moodboardStat,
                                  { color: colors.secondary },
                                ]}
                              >
                                {mb.likes_count} likes
                              </Text>
                              <Text
                                style={[
                                  styles.moodboardStat,
                                  { color: colors.secondary },
                                ]}
                              >
                                {mb.views_count} views
                              </Text>
                              <Text
                                style={[
                                  styles.moodboardStat,
                                  {
                                    color: mb.is_public
                                      ? palette.cyanLimeGreen
                                      : palette.darkGrayBlue,
                                  },
                                ]}
                              >
                                {mb.is_public ? 'Public' : 'Private'}
                              </Text>
                            </View>
                          </View>
                          <Text
                            style={[
                              styles.listItemDate,
                              { color: colors.secondary },
                            ]}
                          >
                            {formatDate(mb.created_at)}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Dates */}
                  <View
                    style={[styles.section, { backgroundColor: colors.card }]}
                  >
                    <Text style={[styles.sectionTitle, { color: colors.text }]}>
                      Dates
                    </Text>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Created:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {formatDate(userDetail.created_at)}
                      </Text>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Last Updated:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {formatDate(userDetail.updated_at)}
                      </Text>
                    </View>
                    <View style={styles.infoRow}>
                      <Text style={[styles.label, { color: colors.secondary }]}>
                        Last Login:
                      </Text>
                      <Text style={[styles.value, { color: colors.text }]}>
                        {userDetail.last_login
                          ? formatDate(userDetail.last_login)
                          : 'Never'}
                      </Text>
                    </View>
                  </View>
                </View>
              ) : (
                <View style={styles.activity}>
                  {userDetail.activity_logs.length === 0 ? (
                    <View style={styles.emptyState}>
                      <Ionicons
                        name="time-outline"
                        size={48}
                        color={colors.secondary}
                      />
                      <Text
                        style={[
                          styles.emptyStateText,
                          { color: colors.secondary },
                        ]}
                      >
                        No activity logs found
                      </Text>
                    </View>
                  ) : (
                    userDetail.activity_logs.map((log: any) => (
                      <View
                        key={log.id}
                        style={[
                          styles.activityItem,
                          {
                            backgroundColor: colors.card,
                            borderLeftColor: colors.primary,
                          },
                        ]}
                      >
                        <View style={styles.activityHeader}>
                          <Text
                            style={[
                              styles.activityAction,
                              { color: colors.text },
                            ]}
                          >
                            {log.action}
                          </Text>
                          <Text
                            style={[
                              styles.activityType,
                              { color: colors.secondary },
                            ]}
                          >
                            {log.action_type}
                          </Text>
                        </View>
                        {log.details && (
                          <Text
                            style={[
                              styles.activityDetails,
                              { color: colors.secondary },
                            ]}
                          >
                            {JSON.stringify(log.details, null, 2)}
                          </Text>
                        )}
                        <View style={styles.activityFooter}>
                          {log.ip_address && (
                            <Text
                              style={[
                                styles.activityMeta,
                                { color: colors.secondary },
                              ]}
                            >
                              IP: {log.ip_address}
                            </Text>
                          )}
                          <Text
                            style={[
                              styles.activityMeta,
                              { color: colors.secondary },
                            ]}
                          >
                            {formatDate(log.created_at)}
                          </Text>
                        </View>
                      </View>
                    ))
                  )}
                </View>
              )}
            </ScrollView>
          ) : null}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    maxHeight: '85%',
    borderRadius: 12,
    ...Platform.select({
      web: {
        maxWidth: 550,
        width: '100%',
        maxHeight: Platform.OS === 'web' ? ('85vh' as any) : '85%',
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    maxHeight: '100%',
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  overview: {
    padding: 12,
  },
  section: {
    padding: 12,
    borderRadius: 6,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  label: {
    fontSize: 13,
    fontWeight: '500',
  },
  value: {
    fontSize: 13,
    flex: 1,
    textAlign: 'right',
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 6,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 11,
    marginTop: 3,
  },
  listItem: {
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  listItemText: {
    fontSize: 13,
    fontWeight: '500',
  },
  listItemDate: {
    fontSize: 11,
    marginTop: 3,
  },
  moodboardInfo: {
    flex: 1,
  },
  moodboardStats: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  moodboardStat: {
    fontSize: 12,
  },
  activity: {
    padding: 12,
  },
  activityItem: {
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
    borderLeftWidth: 3,
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  activityAction: {
    fontSize: 13,
    fontWeight: '600',
  },
  activityType: {
    fontSize: 11,
    textTransform: 'uppercase',
  },
  activityDetails: {
    fontSize: 11,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'mono',
    marginBottom: 6,
  },
  activityFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  activityMeta: {
    fontSize: 10,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyStateText: {
    marginTop: 12,
    fontSize: 14,
  },
});

export default UserDetailModal;
